<template>
  <div class="catalog-view" :class="{ 'sidebar-collapsed': leftSidebarCollapsed }" v-if="hasPermission('roles:read')">
    <div class="left-sidebar">
      <div class="toggle-button d-flex justify-content-center align-items-center" @click="leftSidebarCollapsed = !leftSidebarCollapsed">
        <i class="fas fa-caret-right" v-if="leftSidebarCollapsed"></i>
        <i class="fas fa-caret-left" v-else></i>
      </div>
      <div class="catalog-name">
        <i class="fad fa-user-ninja"></i>
        <span>Roles</span>
      </div>

      <!-- FILTERS AND VIEWS -->
      <div class="filters" v-if="currentAction === crudActions.READ">
        <div class="filter-items">
          <h3>Filtro de búsqueda</h3>

          <div class="filter-item" v-if="canFilterByDomain">
            <label>Dominio</label>
            <b-form-group style="position: relative">
              <b-form-input
                type="text"
                class="shadow-none"
                placeholder="Seleccione el dominio"
                v-model.trim="searchDomain"
                :maxlength="255"
                autocomplete="off"
                @click="showDomainSelection(true)"
                @focus="showDomainSelection(true)"
                @blur="showDomainSelection(false)">
              </b-form-input>
              <div class="dropdown-input-selection-list" v-if="domainSelectionOpen" style="position: relative; top: 0px; z-indez: 3">
                <table class="table">
                  <tbody>
                    <tr v-for="it in domains" :key="it.id" @click="selectDomain(it)" v-show="isMatchDomain(it)">
                      <td>{{ it.name }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-form-group>
          </div>

          <div class="filter-item">
            <label >Dominio, Nombre, Copiado de Rol</label>
            <b-input-group>
              <b-form-input
                id="filter-input"
                v-model="searchBy"
                type="search"
                placeholder="Buscar ...">
              </b-form-input>
              <b-input-group-prepend is-text>
                  <i class="fal fa-search"></i>
              </b-input-group-prepend>
            </b-input-group>
          </div>
        </div>

        <b-row class="main-actions">
          <b-overlay
            class="main-actions__overlay"
            spinner-type="grow"
            spinner-small
            :show="loadingData">
            <b-col cols="6">
              <b-button block size="sm" class="shadow-sm" @click="cleanFilter" :disabled="loadingData">Limpiar</b-button>
            </b-col>
            <b-col cols="6">
              <b-button variant="primary" block size="sm" class="shadow-sm" @click="filterItems" :disabled="loadingData">Buscar</b-button>
            </b-col>
          </b-overlay>
        </b-row>
      </div>

      <div class="short-list" v-if="currentAction !== crudActions.READ">
        <h5>Registros:</h5>
        <b-list-group>
          <b-list-group-item
            class="text-truncate"
            v-for="item in filteredItems"
            :title="item.id + ' - ' + item.name"
            :key="item.id"
            :active="selectedItem == item"
            @click="selectItem(item)">
            {{ item.name }}
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <div class="right-side">
      <div class="grid">
        <!-- TOP BUTTONS BAR -->
        <div class="top-tool-bar">
          <div class="action d-none d-lg-block" @click="newItem" v-if="hasPermission('roles:create')"><i class="fal fa-file-plus"></i> Nuevo</div>
          <div class="action d-none d-lg-block" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i> Detalle</div>
          <div class="action d-none d-lg-block" @click="updateItem" v-if="hasPermission('roles:update')" :class="{ disabled: !canUpdate }"><i class="fal fa-file-edit"></i> Modificar</div>
          <div class="action d-none d-lg-block" @click="deleteItem" v-if="hasPermission('roles:delete')" :class="{ disabled: !canDelete }"><i class="fal fa-trash-alt"></i> Eliminar</div>
          <div class="action d-none d-lg-block" title="Descargar registros" @click="printCsv"><i class="fal fa-file-spreadsheet"></i> Exportar</div>
          <div class="action d-none d-lg-block" title="Refrescar tabla para obtener las últimas actualizaciones" @click="updateData"><i class="fal fa-sync"></i> Refrescar</div>
          <div class="actions-collapsed d-lg-none">
            <b-dropdown class="shadow-sm" text="Acciones">
              <b-dropdown-item @click="newItem" v-if="hasPermission('roles:create')">Nuevo</b-dropdown-item>
              <b-dropdown-item @click="queryItem" v-if="selectedItem.id">Detalle</b-dropdown-item>
              <b-dropdown-item @click="updateItem" v-if="canUpdate">Modificar</b-dropdown-item>
              <b-dropdown-item @click="deleteItem" v-if="canDelete">Eliminar</b-dropdown-item>
              <b-dropdown-item @click="printCsv">Exportar</b-dropdown-item>
              <b-dropdown-item @click="updateData">Refrescar</b-dropdown-item>
            </b-dropdown>
          </div>
          <TopToolBarPaginator :key="paginatorKey" :totalItems="filteredItems.length" :pageSize="PAGE_SIZE_VISIBLE_ITEMS" @pageChange="onPageChange" v-b-tooltip.hover.left="'Total de registros: ' + filteredItems.length"/>
        </div>

        <!-- DATA TABLE -->
        <div class="table-container">
          <table class="data-table table table-hover">
            <thead>
              <tr>
                <template v-for="(col, index) in roleColumns">
                  <th :key="index">
                    <div>
                      {{ col.name }}
                    </div>

                    <div v-if="col.sort" class="sort" @click="sortItems(col)">
                      <i class="fad fa-sort"></i>
                    </div>
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredItems" :key="item.id" @click="selectItem(item)" @dblclick="selectItemAndShowDetail(item)" :class="{ 'selected': isItemSelected(item) }" v-show="isRowVisibleInPage(index)">
                <template v-for="(col, index) in roleColumns">
                  <template v-if="index === 0">
                    <th :key="index">
                      {{ item[col.key] }}
                    </th>
                  </template>
                  <template v-else>
                    <td :key="index">
                      <div :title="item[col.key] && item[col.key].length > 40 ? item[col.key] : ''">{{ item[col.key] }}</div>
                    </td>
                  </template>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      <!-- CONSULTA DE DETALLE -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.READ_DETAIL">
          <AdminRoleForm :id="selectedItem.id" :key="selectedItem.id" :readOnly="true" :title="'Role ' + selectedItem.name" @close="onCancelForm" @saved="onItemSaved" @edit-mode="changeToEditMode" @delete-item="deleteItem"/>
        </div>
      </transition>

      <!-- NUEVO REGISTRO -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.CREATE">
          <AdminRoleForm :id="selectedItem.id" :key="selectedItem.id" title="Nuevo Role" @close="currentAction = crudActions.READ" @saved="onItemSaved" />
        </div>
      </transition>

      <!-- ACTUALIZACIÓN -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.UPDATE">
          <AdminRoleForm :id="selectedItem.id" :key="selectedItem.id" :title="'Modificando ' + selectedItem.name" @close="currentAction = crudActions.READ" @saved="onItemSaved" />
        </div>
      </transition>

      <!-- LOADING MASK -->
      <div class="loading-mask" v-show="loadingData">
        <i class="far fa-spinner-third fa-spin"></i>
      </div>
    </div>

    <a class="hidden-csv-download-link" ref="csvDownload"></a>
  </div>
  <div v-else>
    <div id="not-sufficent-permissions" class="page-wrap d-flex flex-row align-items-center">
        <div class="container">
          <div class="row justify-content-center">
              <div class="col-md-12 text-center">
                  <span class="display-1 d-block">403</span>
                  <div class="mb-4 lead">Lo sentimos, no tienes permisos suficientes para consultar estos registros.</div>
              </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import AdminRoleForm from '@/components/admin/AdminRoleForm.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, RoleCatalogFilters, RoleCatalogGridItem, Role, CatalogGridItemColumn, DomainCatalogFilters, Domain, BasicEntity } from '@/types/Catalogs'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminRoleForm,
    MessageError,
    TopToolBarPaginator
  }
})
export default class AdminRoles extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  roleColumns = [
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('domain', 'Dominio', true),
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('maxSessions', 'Sesiones máximas', true),
    CatalogGridItemColumn.def('copyFromRole', 'Copiado del Rol', true),
    CatalogGridItemColumn.def('creation', 'Fecha de creación', true),
    CatalogGridItemColumn.def('update', 'Fecha de actualización', true)
  ]
  totalItems: number = 0
  items: RoleCatalogGridItem[] = []
  filteredItems: RoleCatalogGridItem[] = []
  filters = new RoleCatalogFilters()
  selectedItem = new RoleCatalogGridItem()
  searchBy = ''

  async created () {
    this.updateData()
    if (this.canFilterByDomain) {
      await this.loadDomains(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
    }
  }

  /* User permissions */

  /**
   * Check if user has 'permission' permission
   * @param permission name to check
   */
  hasPermission (permision: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permision)
  }

  /**
   * Checks if the user can view domain filter, for this
   * the user must belongs to root domain and has domains read permission.
   */
  get canFilterByDomain () {
    return userInfoStore.inRootDomain() && this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /**
   * Checks what records user can update
   */
  get canUpdate () {
    return this.selectedItem.id && this.hasPermission(PermissionNames.ROLES_UPDATE)
  }

  /**
   * Checks what records user can delete
   */
  get canDelete () {
    return this.selectedItem.id && this.hasPermission(PermissionNames.ROLES_DELETE)
  }

  /**
   * Clear and update all permission records
   */
  async updateData () {
    if (this.loadingData) return
    this.loadingData = true
    this.items = []
    const pageRequest = new PageRequest<RoleCatalogFilters>(this.PAGE_SIZE)
    pageRequest.filters = this.filters
    this.loadPage(pageRequest)
  }

  /**
   * Get all role records and assign them in 'items' variable
   * @param pageRequest - configurations to request
   */
  loadPage (pageRequest: PageRequest<any>) {
    catalogsService.getRoles(pageRequest).then(response => {
      this.items.push(...response.results.map((it: Role): RoleCatalogGridItem => {
        return this.fillGridItem(it, new RoleCatalogGridItem())
      }))
      if (this.items.length < response.total) {
        pageRequest.page++
        this.loadPage(pageRequest)
      } else {
        this.loadingData = false
        this.filteredItems = this.items
        this.totalItems = this.filteredItems.length
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de roles: ${ToString(error.response ?? error)}`)
      this.loadingData = false
    })
  }

  /**
   * Gets all domains records and assing them in 'domains' variable
   * @param pageRequest - request configurations
   */
  async loadDomains (pageRequest: PageRequest<any>) {
    this.loadingDomains = true
    catalogsService.getDomains(pageRequest).then((response) => {
      this.domains.push(...response.results.map((it: Domain) => {
        return { id: it.id, name: it.name } as BasicEntity
      }))
      if (this.domains.length < response.total) {
        pageRequest.page++
        this.loadDomains(pageRequest)
      }
    }).finally(() => {
      this.loadingDomains = false
      this.selectDefaultDomain()
    })
  }

  /**
   * Create a RoleCatagolGridItem with base on Role
   * @param origin - to obtains values
   * @param target - RoleCatalogGridItem to fill with origin object values
   */
  fillGridItem (origin: Role, target: RoleCatalogGridItem) {
    target.id = origin.id
    target.copyFromRole = origin.copyFromRole
    target.maxSessions = origin.maxSessions
    target.name = origin.name
    target.domain = origin.domain
    target.creation = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.update = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    return target
  }

  /**
   * Select the current item on double clic.
   * @param item - row selected on table
   */
  selectItemAndShowDetail (item: any) {
    this.currentAction = CrudActions.READ_DETAIL
    this.selectItem(item)
  }

  /**
   * Select the current item and assign it in 'selectedItem' variable.
   * @param item - row selected on table
   */
  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.DELETE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.hasPermission(PermissionNames.ROLES_UPDATE) ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  /**
   * Check if 'item' is the current item selected
   * @param item - row table
   */
  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  /** Clear the current selected item */
  cleanItemSelected () {
    this.selectedItem = new RoleCatalogGridItem()
  }

  /**
   * This triggers a form view transition event to read role
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * This triggers a form view transition event to create role
   */
  newItem () {
    this.selectedItem = new RoleCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  /**
   * This triggers a form view transition event to update role
   */
  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  /**
   * Delete the current role selected after a confirmation message.
   */
  deleteItem () {
    if (this.selectedItem.id) {
      let messageConfirmation = `Esta por eliminar el registro:\
        "${this.selectedItem.id} - ${this.selectedItem.name}".\
        Esta acción es irreversible y se perderá permanentemente dicho registro.\
        ¿Estás seguro de continuar?`
      this.$bvModal.msgBoxConfirm(messageConfirmation, {
        title: 'Eliminando rol',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Confirmar',
        cancelVariant: 'outline-secondary',
        cancelTitle: 'Cancelar',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        hideHeaderClose: false
      }).then(value => {
        if (value) {
          catalogsService.deleteRoleById(this.selectedItem.id).then((response) => {
            this.$bvToast.toast(`El rol ${this.selectedItem.id} se ha eliminado correctamente`, {
              title: 'Eliminación finalizada',
              variant: 'success',
              toaster: 'b-toaster-top-center',
              autoHideDelay: 3000
            })
          }).catch((error) => {
            let messageValues = {
              title: 'Error al eliminar el rol.',
              message: '',
              delay: 10000,
              error: error
            }
            let errorComponent = new MessageError({ propsData: messageValues })
            errorComponent.displayMessage()
          }).finally(() => {
            this.updateData()
            this.currentAction = CrudActions.READ
          })
        }
      })
    }
  }

  /**
   * Returns the current number of filtered items
   */
  get countTotalItems () {
    return this.filteredItems.length || this.items.length
  }

  /* Domain selector */

  domainSelectionOpen = false
  domainIdSelected = -1
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  searchDomain = ''
  domains: BasicEntity[] = []
  loadingDomains = false

  /**
   * Active domainSelectionOpen flag to display the dropdown domain list
   * and updates the searchDomain and domainSelected values when the dropdown is close
   * @param isOpen flag
   */
  showDomainSelection (isOpen: boolean) {
    if (isOpen) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.name && this.domainSelectedBackup.id !== this.domainSelected.id) {
          this.searchDomain = this.domainSelectedBackup.name
          this.domainSelected = this.domainSelectedBackup
        }
      }, 200)
    }
  }

  /**
   * Validates if a domain name matches with the current searchDomain value
   * @param domain entity to check its name
   */
  isMatchDomain (domain: BasicEntity) {
    if (!this.searchDomain) return true
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  /**
   * Updates domains variables based on 'domain' selected
   * @param domain selected
   */
  selectDomain (domain: BasicEntity) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    this.domainIdSelected = domain.id
  }

  /**
   * Sets the current user domain as default domain selected
   */
  selectDefaultDomain () {
    this.filters.domain = userInfoStore.userInfo.domain.id
    this.domainIdSelected = userInfoStore.userInfo.domain.id
    const domain = this.domains.find(it => it.id === this.domainIdSelected)
    if (domain) this.selectDomain(domain)
  }

  /* Filter and sorting data */

  async filterItems () {
    if (this.filters.domain !== this.domainIdSelected) {
      this.filters.domain = this.domainIdSelected
      await this.updateData()
    }

    const regexSearch = new RegExp(this.searchBy, 'i')
    this.resetPaginator()
    this.filteredItems = this.items.filter(it =>
      (!this.searchBy ||
      regexSearch.test(it.name) ||
      regexSearch.test(it.copyFromRole) ||
      regexSearch.test(it.domain.toString()))
    )
  }

  /**
   * Sort current filtered items with base a specific column
   * @param col - column to filter with
   */
  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  /** Calculates visible index range for pagination */
  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  /** Reset index range pagination and clear current selected item */
  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /**
   * Clear current filter configurations
   */
  cleanFilter () {
    this.searchBy = ''
    this.filters = new RoleCatalogFilters()
    this.filteredItems = this.items
    this.totalItems = this.filteredItems.length
  }

  /** Check if the index of a row is within the visible range  */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  /**
   * Filter funtion to already items
   */
  onFiltered (filteredItems: any) {
    this.totalItems = filteredItems.length
  }

  /* Form */
  /**
   * Switch to edit mode
   */
  changeToEditMode () {
    this.currentAction = CrudActions.UPDATE
  }

  onItemSaved (item: Role) {
    if (!this.selectedItem.id) {
      this.selectedItem = new RoleCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
  }

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  /** Export data */
  printCsv () {
    let csv = ''
      .concat('ID').concat(',')
      .concat('DOMAIN').concat(',')
      .concat('NAME').concat(',')
      .concat('MAX SESSIONS').concat(',')
      .concat('COPY FROM ROLE').concat(',')
      .concat('CREACIÓN').concat(',')
      .concat('ACTUALIZACIÓN').concat('\n')

    for (let it of this.filteredItems) {
      csv += ''
        .concat(it.id.toString()).concat(',')
        .concat(it.domain.toString()).concat(',')
        .concat(it.name).concat(',')
        .concat(it.maxSessions.toString()).concat(',')
        .concat(it.copyFromRole).concat(',')
        .concat(it.creation).concat(',')
        .concat(it.update).concat('\n')
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `Roles.csv`
    element.click()
  }
}
</script>

<style lang="css" scoped>
#not-sufficent-permissions {
    min-height: 100vh;
}
</style>
